import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TransferFlow, TransferTypeValue } from 'utils/constants';
import { Holding, ManualHolding } from '../../api/atv/atvApi.types';
import { RootState } from '../../state/store';
import { TransferFlowStep } from './steps';
import {
  IExternalAccountState,
  IInternalAccountState,
  IManualExternalAccountState,
} from './transferSlice.types';

export const initialIManualExternalAccountState: IManualExternalAccountState = {
  institutionName: '',
  ownerName: '',
  jointOwnerName: '',
  accountNumber: '',
  balance: {
    amount: 0,
    currency: 'USD',
  },
  accountType: null,
};
interface ExternalAccountIncomplete {
  accountNumber?: string | null;
  accountType?: string | null;
  fullName?: string | null;
}

interface TransferState {
  currentStep: number;
  accountType: number | null;
  selectedHoldings: Holding[] | null;
  manualSelectedHoldings: ManualHolding[] | null;
  transferFlow: TransferFlow | null;
  stepHistory: number[];
  transferType: TransferTypeValue;
  selectedExternalAccount: IExternalAccountState | null;
  selectedInternalAccount: IInternalAccountState | null;
  manualExternalAccount: IManualExternalAccountState;
  externalAccountIncomplete?: ExternalAccountIncomplete;
  docusignUrl: string;
}

const initialState: TransferState = {
  currentStep: 1,
  accountType: null,
  selectedHoldings: null,
  manualSelectedHoldings: null,
  transferFlow: null,
  stepHistory: [],
  transferType: TransferTypeValue.FullTransfer,
  selectedExternalAccount: null,
  selectedInternalAccount: null,
  manualExternalAccount: initialIManualExternalAccountState,
  docusignUrl: '',
  externalAccountIncomplete: {
    accountNumber: null,
    accountType: null,
    fullName: null,
  },
};

const transfer = createSlice({
  name: 'transfer',
  initialState,
  reducers: {
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    setAccountType(state, action: PayloadAction<number | null>) {
      state.accountType = action.payload;
    },
    setSelectedHoldings(state, action: PayloadAction<Holding[]>) {
      state.selectedHoldings = action.payload;
    },
    setManualSelectedHoldings(state, action: PayloadAction<ManualHolding[]>) {
      state.manualSelectedHoldings = action.payload;
    },
    setTransferFlow(state, action: PayloadAction<TransferFlow | null>) {
      state.transferFlow = action.payload;
    },
    addStepToHistory(state, action: PayloadAction<number>) {
      state.stepHistory.push(action.payload);
    },
    popLastStepFromHistory(state) {
      state.stepHistory.pop();
    },
    setTransferType(state, action: PayloadAction<TransferTypeValue>) {
      state.transferType = action.payload;
    },
    setSelectedExternalAccount(
      state,
      action: PayloadAction<IExternalAccountState | null>,
    ) {
      state.selectedExternalAccount = action.payload;
    },
    setSelectedInternalAccount(
      state,
      action: PayloadAction<IInternalAccountState | null>,
    ) {
      state.selectedInternalAccount = action.payload;
    },
    setManualExternalAccount(state, action: PayloadAction<IManualExternalAccountState>) {
      state.manualExternalAccount = action.payload;
    },
    setDocusignUrl(state, action: PayloadAction<string>) {
      state.docusignUrl = action.payload;
    },
    setExternalAccountIncomplete(
      state,
      action: PayloadAction<Partial<ExternalAccountIncomplete>>,
    ) {
      state.externalAccountIncomplete = {
        ...state.externalAccountIncomplete,
        ...action.payload,
      };
    },
  },
});

export const selectAccountType = (state: RootState) => state.transfer.accountType;
export const selectSelectedHoldings = (state: RootState) =>
  state.transfer.selectedHoldings;
export const selectManualSelectedHoldings = (state: RootState) =>
  state.transfer.manualSelectedHoldings;
export const selectTransferFlow = (state: RootState) => state.transfer.transferFlow;
export const selectLastStep = (state: RootState) => {
  const { stepHistory } = state.transfer;
  return stepHistory.length > 0
    ? stepHistory[stepHistory.length - 1]
    : TransferFlowStep.CHOOSE_ACCOUNT_TYPE;
};
export const selectTransferType = (state: RootState) => state.transfer.transferType;
export const selectSelectedExternalAccount = (state: RootState) =>
  state.transfer.selectedExternalAccount;
export const selectSelectedInternalAccount = (state: RootState) =>
  state.transfer.selectedInternalAccount;
export const selectManualExternalAccount = (state: RootState) =>
  state.transfer.manualExternalAccount;
export const selectDocusignUrl = (state: RootState) => state.transfer.docusignUrl;

export const {
  setAccountType,
  setCurrentStep,
  setSelectedHoldings,
  setManualSelectedHoldings,
  setTransferFlow,
  addStepToHistory,
  popLastStepFromHistory,
  setTransferType,
  setSelectedExternalAccount,
  setSelectedInternalAccount,
  setManualExternalAccount,
  setDocusignUrl,
  setExternalAccountIncomplete,
} = transfer.actions;
export default transfer.reducer;
